// src/pages/CafePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import BookCard from "../components/BookCard";
import { getFavoriteCafeIds, toggleFavoriteCafe } from "../utils/favoriteCafes";
import { isReservationActive, listReservationsByBookIds } from "../utils/reservations";
import {
  collection, query, where, orderBy,
  limit, startAfter, getDocs, documentId
} from "firebase/firestore";

// 4 у ряд на десктопі
const Styles = () => (
  <style>{`
    .cafe-wrap { background: #F6F4EF; min-height: 100vh; }
    .container { max-width: 1200px; margin: 0 auto; padding: 24px 16px; }

    .top { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
    .h1 { margin: 0 0 6px 0; font-size: 22px; font-weight: 800; }
    .muted { color: #6b7280; }

    /* зелена «пігулка» як на головній */
    .btn-map {
    display:inline-block;
    height: 36px; line-height:36px;
    padding: 0 14px;
    border-radius: 9999px;
    background: #0b5226; color:#fff;
    border: none; cursor: pointer; text-decoration:none;
    font-weight: 700;
    }
    .btn-map:disabled { opacity:.6; cursor: default; }

    /* звичайна кнопка (для "Показати ще") */
    .btn {
    height: 36px; padding: 0 14px; border-radius: 8px;
    border: 1px solid #e5e7eb; background: #fff; cursor: pointer;
    }

    /* грід як на головній: 4/3/2/1 колонки, по центру завдяки .container */
    .results {
    display:grid;
    grid-template-columns: repeat(4, minmax(0,1fr));
    gap: 16px; margin-top: 16px;
    }
    @media (max-width: 1200px) { .results { grid-template-columns: repeat(3, minmax(0,1fr)); } }
    @media (max-width: 900px)  { .results { grid-template-columns: repeat(2, minmax(0,1fr)); } }
    @media (max-width: 540px)  { .results { grid-template-columns: repeat(2, minmax(0,1fr)); } }
    @media (max-width: 360px)  { .results { grid-template-columns: 1fr; } } /* для дуже вузьких */


  `}</style>
);

const PAGE_SIZE = 20;
const chunk = (arr, size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export default function CafePage() {
  const [user] = useAuthState(auth);
  const { name } = useParams();                    // name у URL — це encodeURIComponent(cafeName)
  const cafeName = decodeURIComponent(name || "");
  const [cafeMeta, setCafeMeta] = useState(null);  // address, lat, lng (якщо є)
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [templateCatalog, setTemplateCatalog] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [toastText, setToastText] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef(null);
  const reqKeyRef = useRef(0);

  // тягнемо метадані кав’ярні (адреса/координати) — з колекції cafes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "cafes"), where("cafeName", "==", cafeName), limit(1))
        );
        if (cancelled) return;
        const data = snap.docs[0]?.data();
        setCafeMeta(data ? { id: snap.docs[0].id, ...data } : null);
      } finally {
        /* no-op */
      }
    })();
    return () => { cancelled = true; };
  }, [cafeName]);

  // базовий запит на книги цієї кав’ярні (відразу серверний фільтр, щоб економити ліміти)
  const buildQuery = useMemo(() => (afterDoc = null) => {
    let q = query(
      collection(db, "books"),
      where("status", "==", "available"),
      where("cafeName", "==", cafeName),
      orderBy("createdAtTs", "desc"),
      limit(PAGE_SIZE)
    );
    if (afterDoc) q = query(q, startAfter(afterDoc));
    return q;
  }, [cafeName]);

  const templateCatalogMap = useMemo(() => {
    const map = new Map();
    templateCatalog.forEach((template) => {
      if (template?.id) map.set(template.id, template);
    });
    return map;
  }, [templateCatalog]);

  const mergeTemplateCatalog = (rows = []) => {
    if (!rows.length) return;
    setTemplateCatalog((prev) => {
      const next = new Map(prev.map((template) => [template.id, template]));
      let changed = false;
      rows.forEach((template) => {
        if (!template?.id) return;
        if (!next.has(template.id)) changed = true;
        next.set(template.id, template);
      });
      if (!changed) return prev;
      return Array.from(next.values());
    });
  };

  const loadTemplatesByIds = async (templateIds, myKey) => {
    const ids = Array.from(new Set((templateIds || []).filter(Boolean)));
    if (ids.length === 0) return new Map();

    const map = new Map();
    const missing = [];
    ids.forEach((id) => {
      const cached = templateCatalogMap.get(id);
      if (cached) map.set(id, cached);
      else missing.push(id);
    });

    if (missing.length === 0) return map;

    const fetched = [];
    for (const pack of chunk(missing, 10)) {
      if (!pack.length) continue;
      const tplSnap = await getDocs(
        query(collection(db, "bookTemplates"), where(documentId(), "in", pack))
      );
      if (reqKeyRef.current !== myKey) return null;
      tplSnap.docs.forEach((d) => {
        const row = { id: d.id, ...d.data() };
        map.set(d.id, row);
        fetched.push(row);
      });
    }

    if (fetched.length && reqKeyRef.current === myKey) mergeTemplateCatalog(fetched);
    return map;
  };

  const loadFirstPage = async () => {
    reqKeyRef.current += 1;
    const myKey = reqKeyRef.current;
    setLoading(true);
    setItems([]); setTemplateCatalog([]); setCursor(null); setHasMore(true);
    try {
      const snap = await getDocs(buildQuery(null));
      if (reqKeyRef.current !== myKey) return;
      const page = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const templateIds = Array.from(new Set(page.map((b) => b.templateId).filter(Boolean)));
      const tplMap = await loadTemplatesByIds(templateIds, myKey);
      if (!tplMap) return;
      const mergedPage = page.map((b) => {
        const t = tplMap.get(b.templateId) || {};
        return {
          ...b,
          isbn: b.isbn || t.isbn || "",
          title: b.title || t.title || "",
          author: b.author || t.author || "",
          coverUrl: b.coverUrl || b.cover || t.coverUrl || t.cover || t.imageUrl || t.thumbnailUrl || "",
          borrowPrice: b.borrowPrice ?? b.priceBorrow ?? b.price ?? t.borrowPrice ?? t.priceBorrow ?? t.price ?? null,
          borrowCount: b.borrowCount ?? t.borrowCount ?? 0,
        };
      });
      const reservations = await listReservationsByBookIds(mergedPage.map((b) => b.id));
      if (reqKeyRef.current !== myKey) return;
      const reservedBookIds = new Set(
        reservations
          .filter((r) => isReservationActive(r))
          .map((r) => String(r.bookId || r.id))
      );
      const visiblePage = mergedPage.filter((b) => !reservedBookIds.has(String(b.id)));
      setItems(visiblePage);
      setCursor(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.size === PAGE_SIZE);
    } finally {
      if (reqKeyRef.current === myKey) setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const myKey = reqKeyRef.current;
    try {
      const snap = await getDocs(buildQuery(cursor));
      if (reqKeyRef.current !== myKey) return;
      const page = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const templateIds = Array.from(new Set(page.map((b) => b.templateId).filter(Boolean)));
      const tplMap = await loadTemplatesByIds(templateIds, myKey);
      if (!tplMap) return;
      const mergedPage = page.map((b) => {
        const t = tplMap.get(b.templateId) || {};
        return {
          ...b,
          isbn: b.isbn || t.isbn || "",
          title: b.title || t.title || "",
          author: b.author || t.author || "",
          coverUrl: b.coverUrl || b.cover || t.coverUrl || t.cover || t.imageUrl || t.thumbnailUrl || "",
          borrowPrice: b.borrowPrice ?? b.priceBorrow ?? b.price ?? t.borrowPrice ?? t.priceBorrow ?? t.price ?? null,
          borrowCount: b.borrowCount ?? t.borrowCount ?? 0,
        };
      });
      const reservations = await listReservationsByBookIds(mergedPage.map((b) => b.id));
      if (reqKeyRef.current !== myKey) return;
      const reservedBookIds = new Set(
        reservations
          .filter((r) => isReservationActive(r))
          .map((r) => String(r.bookId || r.id))
      );
      const visiblePage = mergedPage.filter((b) => !reservedBookIds.has(String(b.id)));
      setItems(prev => {
        const seen = new Set(prev.map(x => x.id));
        const merged = prev.slice();
        for (const b of visiblePage) if (!seen.has(b.id)) merged.push(b);
        return merged;
      });
      setCursor(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.size === PAGE_SIZE);
    } finally {
      if (reqKeyRef.current === myKey) setLoadingMore(false);
    }
  };

  useEffect(() => { loadFirstPage(); }, [buildQuery]); // при зміні кав’ярні перезавантажуємо

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showToast = (text) => {
    setToastText(text);
    setToastVisible(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 1700);
  };

  useEffect(() => {
    (async () => {
      if (!user?.uid || !cafeMeta?.id) {
        setIsFavorite(false);
        return;
      }
      try {
        const ids = await getFavoriteCafeIds(user.uid);
        setIsFavorite(ids.includes(cafeMeta.id));
      } catch {
        setIsFavorite(false);
      }
    })();
  }, [user?.uid, cafeMeta?.id]);

  const toggleFavorite = async () => {
    if (!user?.uid) {
      showToast("Увійдіть, щоб додати у вибрані");
      return;
    }
    if (!cafeMeta?.id || favoriteBusy) return;

    setFavoriteBusy(true);
    try {
      const result = await toggleFavoriteCafe(user.uid, cafeMeta.id, user.email || "");
      setIsFavorite(result.isFavorite);
      showToast(result.isFavorite ? "Додано у вибрані" : "Видалено з вибраних");
    } finally {
      setFavoriteBusy(false);
    }
  };

  const mapsHref = (() => {
    // якщо є координати — віддаємо q=lat,lng, інакше — пошук за рядком
    if (cafeMeta?.lat && cafeMeta?.lng) {
      return `https://www.google.com/maps?q=${cafeMeta.lat},${cafeMeta.lng}`;
    }
    const q = [cafeName, cafeMeta?.cafeAddress, cafeMeta?.district].filter(Boolean).join(", ");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  })();

  return (
  <div className="cafe-wrap">
    <Styles />
    <div className="container">
      <div className="top">

        <h1 className="h1">{cafeName}</h1>
        {cafeMeta?.cafeAddress && <span className="muted">· {cafeMeta.cafeAddress}</span>}
        <a className="btn-map" href={mapsHref} target="_blank" rel="noreferrer">Показати на мапі</a>
        <button
          type="button"
          onClick={toggleFavorite}
          disabled={favoriteBusy}
          style={{
            height: 36,
            padding: "0 12px",
            borderRadius: 9999,
            border: "1px solid #e5e7eb",
            background: "#fff",
            color: "#0b5226",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontWeight: 700,
            cursor: favoriteBusy ? "default" : "pointer",
          }}
          title="Добавити у вибрані"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 21s-6.7-4.35-9.2-8.04C.59 9.67 2.13 5.5 6.2 5.5c2.15 0 3.29 1.03 4.2 2.38.91-1.35 2.05-2.38 4.2-2.38 4.07 0 5.61 4.17 3.4 7.46C18.7 16.65 12 21 12 21z"
              fill={isFavorite ? "#dc2626" : "#fff"}
              stroke="#0b5226"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
          {isFavorite ? "У вибраних" : "Добавити у вибрані"}
        </button>
      </div>

      <div className="muted" style={{marginTop:4}}>
        Книги в цій кав’ярні: {items.length}{hasMore ? "…" : ""}
      </div>

      {loading ? (
        <div style={{marginTop:12}}>Завантаження…</div>
      ) : (
        <>
          <div className="results" style={{marginTop:12}}>
            {items.map(b => <BookCard key={b.id} book={b} />)}
          </div>

          <div style={{ marginTop: 16, textAlign: "center" }}>
            {hasMore ? (
              <button className="btn" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "Завантаження…" : "Показати ще"}
              </button>
            ) : (
              items.length > 0 && <div className="muted">Усе показано</div>
            )}
          </div>
        </>
      )}
      </div>{/* /.container */}

      {toastVisible && (
        <div
          style={{
            position: "fixed",
            left: "50%",
            bottom: 22,
            transform: "translateX(-50%)",
            background: "rgba(17, 24, 39, 0.92)",
            color: "#fff",
            borderRadius: 999,
            padding: "10px 14px",
            fontWeight: 600,
            fontSize: 14,
            zIndex: 50,
            boxShadow: "0 8px 28px rgba(0,0,0,0.25)",
          }}
        >
          {toastText}
        </div>
      )}
    </div>
  );
}


